import { useEffect, useRef, useState } from 'react';
import TiktokPixel from 'tiktok-pixel';

import { MirrorService } from 'services/MirrorService';

import { TrackingApi } from 'api/TrackingApi';
import { Gtm } from 'helpers/analytics/gtm';
import { FirebaseNotificationManager } from 'helpers/pushes/FirebaseNotificationManager';
import { getUserIdFromCookies } from 'helpers/userId';
import { assertNumber } from 'helpers/utils/checkType';
import { useI18n } from 'hooks/useI18n';
import { useInitialId } from 'hooks/useInitialId';
import { usePartnerId } from 'hooks/usePartnerId';
import { useRetentionPopunder } from 'hooks/useRetentionPopunder';
import { useSessionStartTrack } from 'hooks/useSessionStartTrack';

export const useInitializeApp = () => {
  const { getInitialId, getInitialIdFromCookie, setInitialIdToCookie } =
    useInitialId();
  const { initI18n, setBuildVersionToSessionStorage } = useI18n();
  const { trackStartSession } = useSessionStartTrack();
  const { initRetentionPopunder } = useRetentionPopunder();
  const { isOrganicPartnerIdOnMainMirror } = usePartnerId();

  const [isInitialized, setIsInitialized] = useState(false);

  const isNewUser = useRef(!getInitialIdFromCookie());

  const initGTM = () => {
    if (MirrorService.gtmId) {
      const userId = getUserIdFromCookies();

      if (assertNumber(userId)) {
        Gtm.userIdEvent(userId);
      }

      Gtm.init({ gtmId: MirrorService.gtmId });
    }
  };

  const initTiktok = () => {
    if (MirrorService.isDatesMe && MirrorService.tiktokId) {
      TiktokPixel.init(MirrorService.tiktokId);
    }
  };

  const initFirebase = () => {
    if (isOrganicPartnerIdOnMainMirror) return;

    FirebaseNotificationManager.requestPushPermissionsOnStartupAndTrack();

    if (!isNewUser.current) {
      FirebaseNotificationManager.tryToUpdateTokenAndSendItToServer();
    }
  };

  useEffect(() => {
    const initialize = async () => {
      initGTM();
      initTiktok();

      try {
        const initialId = await getInitialId();

        setInitialIdToCookie(initialId);
      } catch (err: any) {
        TrackingApi.trackError({
          type: 'error',
          message: `set initial_id fail: ${err?.message}`,
        });
      }

      const newTranslationsBuildVersion = await trackStartSession(true);

      if (newTranslationsBuildVersion) {
        setBuildVersionToSessionStorage(newTranslationsBuildVersion);
      }

      try {
        await initI18n();

        initRetentionPopunder();

        setIsInitialized(true);
      } catch (err: any) {
        TrackingApi.trackError({
          type: 'error',
          message: `initI18n initialization: ${err?.message}`,
        });
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitialized) {
      initFirebase();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]);

  return {
    isInitialized,
  };
};
